'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import Image from 'next/image'
import { useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { PatternFormat } from 'react-number-format'
import { toast } from 'sonner'
import * as z from 'zod'

import CheckboxInput from '@/_components/inputs/checkbox-input'

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '../../../_components/ui'
import { sendNewPartnerContactEmail } from '../_actions/send-clinic-email-contact'
import { becomePartnerSchema } from '../_actions/send-clinic-email-contact/schema'
import { SectionColumn, SectionHeaderTitle, SectionText } from './section'
import TermsOfUse from './terms-of-use'

interface BecomePartnerDialogProps {
  setIsDialogOpen: (isOpen: boolean) => void
}

export const BecomePartnerDialog = ({
  setIsDialogOpen,
}: BecomePartnerDialogProps) => {
  const form = useForm<z.infer<typeof becomePartnerSchema>>({
    resolver: zodResolver(becomePartnerSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      termsOfUse: false,
    },
    shouldUnregister: true,
  })

  const [isPending, startTransition] = useTransition()

  const onSubmit = async (data: z.infer<typeof becomePartnerSchema>) => {
    startTransition(async () => {
      try {
        await sendNewPartnerContactEmail(data)
        setIsDialogOpen(false)
        toast.success('Agradecemos seu cadastro!', {
          description:
            'Em breve entraremos em contato via e-mail ou WhatsApp para prosseguirmos.',
        })
      } catch (error) {
        toast.error('Erro ao enviar o formulário', {
          description:
            'Algo deu errado. Por favor, tente novamente ou entre em contato conosco pelo e-mail contato@doutorsim.com.br.',
        })
      }
    })
  }

  return (
    <>
      <SectionColumn className="items-center gap-4 space-y-1.5 text-center">
        <Image
          src="/icons/logo-text.svg"
          width={147}
          height={31}
          alt="Logo da Dr.Sim"
        />

        <SectionColumn>
          <SectionHeaderTitle className="text-xl md:text-xl">
            Venha dizer SIM com a gente!
          </SectionHeaderTitle>

          <SectionText className="md:text-sm">
            Preencha as informações abaixo para podermos entrar em contato com
            você para explicar como seguiremos.
          </SectionText>
        </SectionColumn>
      </SectionColumn>

      <Form {...form}>
        <form
          className="flex flex-col gap-6"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome da Instituição</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Insira o nome"
                    {...field}
                    disabled={isPending}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>E-mail</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Insira o e-mail"
                    {...field}
                    disabled={isPending}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telefone para contato</FormLabel>
                <FormControl>
                  <PatternFormat
                    format="(##) #####-####"
                    autoComplete="tel-national"
                    customInput={Input}
                    onChange={field.onChange}
                    value={field.value}
                    getInputRef={field.ref}
                    placeholder="(99) 9999-9999"
                    disabled={isPending}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="termsOfUse"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <TermsOfUse
                    id="check"
                    checkboxComponent={
                      <CheckboxInput
                        onChange={field.onChange}
                        disabled={isPending}
                        id="check"
                      />
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enviar
          </Button>
        </form>
      </Form>
    </>
  )
}
